// components/DailyPractice.js — three-pass calm: readiness → today → drill-in
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { apiService, handleApiError } from '../services/api';
import {
  Flame, Clock, ChevronRight, AlertTriangle, CheckCircle, Play
} from 'lucide-react';
import { motion } from 'framer-motion';
import Loading from './common/Loading';
import { Button, Card, Breadcrumbs, LevelDot } from './ui';

// Map study-plan reason → token + label. Color cue, not a badge.
const REASON = {
  review:     { dot: 'warn',    label: 'Review' },
  strengthen: { dot: 'bad',     label: 'Strengthen' },
  discover:   { dot: 'info',    label: 'Discover' },
};

// Readiness ring — kept as the product's signature visual, but monochrome accent.
function ReadinessRing({ score }) {
  const circumference = 2 * Math.PI * 54;
  const offset = circumference - (score / 100) * circumference;
  return (
    <div className="relative w-36 h-36 flex-shrink-0">
      <svg className="w-36 h-36 -rotate-90" viewBox="0 0 120 120">
        <circle cx="60" cy="60" r="54" strokeWidth="8" className="fill-none stroke-line-subtle" />
        <circle
          cx="60" cy="60" r="54" strokeWidth="8"
          strokeLinecap="round"
          className="fill-none stroke-accent transition-all duration-1000"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-bold text-ink tabular-nums">{Math.round(score)}%</span>
        <span className="text-xs text-mutedink">ready</span>
      </div>
    </div>
  );
}

// Single-line streak — same pattern as Mastery, no anxiety triggers.
function StreakLine({ streak }) {
  if (!streak || streak.current_streak === 0) return null;
  return (
    <div className="flex items-center gap-2 text-sm text-mutedink">
      <Flame className="w-4 h-4 text-amber-500" />
      <span>
        <span className="text-ink font-medium">{streak.current_streak}-day</span> streak
        {streak.streak_at_risk && (
          <span className="text-status-warn"> · keep it going today</span>
        )}
      </span>
    </div>
  );
}

// Per-domain readiness bar — single accent.
function DomainReadinessBar({ domain }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-32 text-sm text-read truncate">{domain.domain_name}</div>
      <div className="flex-1 bg-line-subtle rounded-full h-1.5 overflow-hidden">
        <div
          className="h-1.5 bg-accent transition-all duration-700"
          style={{ width: `${domain.readiness}%` }}
        />
      </div>
      <div className="w-10 text-right text-sm tabular-nums text-read">{domain.readiness}%</div>
      <div className="w-14 text-right text-xs text-faint tabular-nums hidden sm:block">
        {Math.round(domain.weight * 100)}% wt
      </div>
    </div>
  );
}

// One plan item = one Card row. LevelDot for reason cue, no colored badges.
function PlanItem({ item, courseId, index }) {
  const reason = REASON[item.reason] || REASON.discover;
  const link = item.exercise_type === 'sandbox'
    ? `/course/${courseId}/sandbox/${item.domain}`
    : `/course/${courseId}/quiz/${item.domain}`;

  const days = item.last_tested
    ? Math.floor((Date.now() - new Date(item.last_tested).getTime()) / 86400000)
    : null;
  let lastTested = null;
  if (days !== null) {
    lastTested = days === 0 ? 'today' : `${days}d ago`;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.04 }}
    >
      <Link
        to={link}
        className="group flex items-center gap-3 px-4 py-3 bg-surface border border-line hover:border-line-strong rounded-lg transition-colors"
      >
        <LevelDot color={reason.dot} size="md" />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-sm font-medium text-ink">
              {item.concept.replace(/_/g, ' ')}
            </span>
            <span className="text-xs text-faint">{reason.label}</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-mutedink mt-0.5">
            <span className="truncate">{item.domain_name}</span>
            {item.mastery_score > 0 && (
              <>
                <span className="text-faint">·</span>
                <span className="tabular-nums">{item.mastery_score.toFixed(1)}/10</span>
              </>
            )}
            {lastTested && (
              <>
                <span className="text-faint">·</span>
                <span>{lastTested}</span>
              </>
            )}
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-faint group-hover:text-mutedink transition-colors flex-shrink-0" />
      </Link>
    </motion.div>
  );
}

export default function DailyPractice() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPlan = async () => {
      try {
        const res = await apiService.getStudyPlan(courseId);
        setData(res.data.data);
      } catch (err) {
        setError(handleApiError(err));
      } finally {
        setLoading(false);
      }
    };
    fetchPlan();
  }, [courseId]);

  if (loading) return <Loading message="Preparing your study plan..." />;

  if (error) {
    return (
      <div className="min-h-screen bg-surface-muted flex items-center justify-center">
        <div className="text-center space-y-4">
          <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto" />
          <p className="text-mutedink">{error}</p>
          <Button onClick={() => navigate(-1)} variant="ghost">Go back</Button>
        </div>
      </div>
    );
  }

  const { readiness, today, streak, course } = data;

  // Inline counts — only show non-zero, mirror Mastery's "what to do" line.
  const counts = [];
  if (today.total_due_for_review > 0) counts.push({ n: today.total_due_for_review, label: 'due for review', tone: 'text-status-warn' });
  if (today.total_weak > 0)            counts.push({ n: today.total_weak,            label: 'to strengthen', tone: 'text-status-bad' });
  if (today.total_untested > 0)        counts.push({ n: today.total_untested,        label: 'to discover',   tone: 'text-read' });

  return (
    <div className="min-h-screen bg-surface-muted">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 space-y-8">

        <Breadcrumbs items={[
          { label: 'My Courses', to: '/my-courses' },
          { label: course.name, to: `/course/${courseId}` },
          { label: "Today's practice" },
        ]} />

        {/* Pass 1 — readiness ring + message + streak. The big "Today's
            practice" H1 was dropped — the breadcrumb already names the
            page. The readiness message becomes the dominant line. */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-6"
        >
          <ReadinessRing score={readiness.score} />
          <div className="flex-1 min-w-0 space-y-2">
            <p className="text-read">{readiness.message}</p>
            <StreakLine streak={streak} />
          </div>
        </motion.div>

        {/* Pass 2 — what to do, inline counts */}
        {counts.length > 0 && (
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm">
            {counts.map(c => (
              <span key={c.label} className={c.tone}>
                <span className="font-medium tabular-nums">{c.n}</span> {c.label}
              </span>
            ))}
            <span className="text-faint">·</span>
            <span className="text-mutedink">~{today.estimated_minutes} min total</span>
          </div>
        )}

        {/* Pass 3a — today's plan */}
        {today.items.length > 0 ? (
          <div className="space-y-2">
            <h2 className="text-xs uppercase tracking-wide text-faint font-normal">
              Today's plan ({today.items.length})
            </h2>
            <div className="space-y-2">
              {today.items.map((item, i) => (
                <PlanItem key={`${item.concept}-${item.domain}`} item={item} courseId={courseId} index={i} />
              ))}
            </div>
          </div>
        ) : (
          <Card className="p-8 text-center">
            <CheckCircle className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
            <h3 className="font-medium text-ink mb-1">All caught up</h3>
            <p className="text-mutedink text-sm mb-4">No concepts due for review. Try a quiz to keep progressing.</p>
            <Button to={`/course/${courseId}/quick-quiz`} variant="primary">
              <Play className="w-4 h-4" /> Quick quiz
            </Button>
          </Card>
        )}

        {/* Pass 3b — domain readiness bars */}
        {readiness.domains && readiness.domains.length > 0 && (
          <div className="space-y-2">
            <h2 className="text-xs uppercase tracking-wide text-faint font-normal">
              Readiness by domain
            </h2>
            <Card className="p-5 space-y-3">
              {readiness.domains.map(d => (
                <DomainReadinessBar key={d.domain} domain={d} />
              ))}
            </Card>
          </div>
        )}

        {/* Bottom nav — secondary buttons */}
        <div className="flex gap-3 pt-2">
          <Button to={`/course/${courseId}/mastery`} variant="secondary" className="flex-1">
            Mastery details
          </Button>
          <Button to={`/course/${courseId}`} variant="secondary" className="flex-1">
            Course dashboard
          </Button>
        </div>
      </div>
    </div>
  );
}
